import React, { useState, useMemo, ChangeEvent, useEffect } from 'react';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridColDef,
  GridRowsProp,
  esES,
} from '@mui/x-data-grid';
import { TextField, Box, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';

interface DataGridWithSearchAndExportProps {
  columns: GridColDef[];
  typeReport: string;
  promoterName: string;
  rows: GridRowsProp;
}

const DataGridWithSearchAndExport: React.FC<DataGridWithSearchAndExportProps> = ({
  columns,
  typeReport,
  promoterName,
  rows,
}) => {
  const [searchText, setSearchText] = useState<string>('');

  let filteredRows = rows;

  // Manejo de la búsqueda
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);

    const filtered = rows.filter((row) =>
      Object.values(row).some((field) => field && field.toString().toLowerCase().includes(value)),
    );

    filteredRows = filtered;
  };

  // Función para copiar al portapapeles
  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value).then(
      () => {
        alert(`Copiado: ${value}`);
      },
      () => {
        alert('Error al copiar al portapapeles');
      },
    );
  };

  const openVideo = (url: string) => {
    window.open(url, '_blank');
  };

  const enhancedColumns = useMemo(() => {
    return columns.map((col) => {
      if (
        col.field === 'creditId' ||
        col.field === 'lendusId' ||
        col.field === 'depositId' ||
        col.field === 'conciliationId' ||
        col.field === 'id' ||
        col.field === 'referenceNumber'
      ) {
        return {
          ...col,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          renderCell: (params: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{col.field === 'referenceNumber' && params.value}</span>
              <Tooltip title={params.value}>
                <IconButton onClick={() => copyToClipboard(params.value)} size="small">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        };
      } else if (col.field === 'videoUrl') {
        return {
          ...col,
          renderCell: (params: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{col.field === 'referenceNumber' && params.value}</span>
              <Tooltip
                title={`https://lending-app-storage-70710978225641-dev.s3.us-east-2.amazonaws.com/${params.value}`}
              >
                <IconButton
                  onClick={() =>
                    openVideo(
                      `https://lending-app-storage-70710978225641-dev.s3.us-east-2.amazonaws.com/${params.value}`,
                    )
                  }
                  size="small"
                >
                  <PlayCircleFilledIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        };
      }
      return col;
    });
  }, [columns]);

  // Define el nombre del archivo para la exportación
  const getExportFilename = () => {
    const baseName = promoterName ? `Cartera de ${promoterName}` : 'Reporte';
    // Opcional: añadir tipo de reporte y fecha si lo deseas
    // const reportTypeName = typeReport === '1' ? 'PagosRegistrados' : typeReport === '2' ? 'DepositosAplicados' : 'Creditos';
    // const date = new Date().toISOString().slice(0, 10); // Formato YYYY-MM-DD
    return `${baseName}.csv`; // Ejemplo: "Cartera de Juana.csv"
  };

  return (
    <Box sx={{ height: 700, width: '100%' }}>
      {/* Buscador */}
      <TextField
        label="Buscar"
        variant="outlined"
        value={searchText}
        onChange={handleSearch}
        sx={{ marginBottom: 2 }}
      />

      {/* DataGrid */}
      <DataGrid
        style={{ height: '100%' }}
        columns={enhancedColumns}
        rows={filteredRows}
        pagination
        pageSizeOptions={[5, 10, 25, 100]}
        getRowId={(row) => row.id || row.clientName}
        //autoHeight
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        disableRowSelectionOnClick
        slots={{
          toolbar: GridToolbarContainer,
        }}
        slotProps={{
          toolbar: {
            children: (
              <GridToolbarExport
                printOptions={{ disableToolbarButton: true }}
                csvOptions={{ fileName: getExportFilename() }} // Usar la función para el nombre
              />
            ),
          },
        }}
      />
    </Box>
  );
};

export default DataGridWithSearchAndExport;
